import React, { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import './index.scss';
import App from './app';
import {BrowserRouter} from 'react-router-dom';
import {Provider} from 'react-redux';
import {store} from './store';
import './services/i18n';
import {PublicClientApplication} from '@azure/msal-browser';
import {MsalProvider} from '@azure/msal-react';
import {msalConfig} from './config/auth-config';
import { ReactKeycloakProvider } from '@react-keycloak/web';
import { createKeycloakClient, initOptions } from './config/keycloak/Keycloak';
import KeycloakAppRoutes from './config/routes/KeycloakAppRoutes';
import { isAzureIDP } from './utils/loginProvider';
import { AlertProvider } from './provider/alert/AlertProvider';
import { hasAzureAuthConfig, hasKeycloakAuthConfig } from './config/runtime-config';
import { clearAccessTokenLocalStorage, setAccessTokenLocalStorage } from './utils/token';

const ConfigError = ({message}: {message: string}) => (
  <div className="d-flex min-vh-100 align-items-center justify-content-center px-4 text-center">
    {message}
  </div>
);

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

const handleKeycloakTokens = ({ token }: { token?: string }) => {
  if (token) {
    setAccessTokenLocalStorage(token);
  }
};

const handleKeycloakEvent = (eventType: string) => {
  if (eventType === 'onAuthLogout' || eventType === 'onAuthRefreshError') {
    clearAccessTokenLocalStorage();
  }
};

if (isAzureIDP && !hasAzureAuthConfig()) {
  root.render(
     <ConfigError message="Missing Azure runtime configuration. Check APP_CONFIG in /config.js." />
  );
} else if (!isAzureIDP && !hasKeycloakAuthConfig()) {
  root.render(
     <ConfigError message="Missing Keycloak runtime configuration. Check APP_CONFIG in /config.js." />
  );
} else if (isAzureIDP) {
  root.render(
    <MsalProvider instance={new PublicClientApplication(msalConfig)}>
      <Provider store={store}>
        <BrowserRouter>
          <AlertProvider>
            <App />
          </AlertProvider>
        </BrowserRouter>
      </Provider>
    </MsalProvider>,
  );
} else {
  root.render(
    <Provider store={store}>
      <ReactKeycloakProvider
        authClient={createKeycloakClient()}
        initOptions={initOptions}
        onTokens={handleKeycloakTokens}
        onEvent={handleKeycloakEvent}
      >
        <Suspense fallback={<div></div>}>
          <AlertProvider>
            <KeycloakAppRoutes />
          </AlertProvider>
        </Suspense>
      </ReactKeycloakProvider>
    </Provider>,
  );
}
